import { css } from '@emotion/css';
import { type GrafanaTheme2 } from '@grafana/data';
import { useChromeHeaderHeight } from '@grafana/runtime';
import {
  behaviors,
  sceneGraph,
  SceneObjectBase,
  SceneVariableSet,
  type QueryVariable,
  type SceneComponentProps,
  type SceneObjectState,
} from '@grafana/scenes';
import { useStyles2 } from '@grafana/ui';
import React from 'react';

import { type DataTrail } from 'AppDataTrail/DataTrail';
import { EventQuickSearchChanged } from 'MetricsReducer/list-controls/QuickSearch/EventQuickSearchChanged';
import { QuickSearch } from 'MetricsReducer/list-controls/QuickSearch/QuickSearch';
import { EventMetricsVariableActivated } from 'MetricsReducer/metrics-variables/events/EventMetricsVariableActivated';
import { EventMetricsVariableDeactivated } from 'MetricsReducer/metrics-variables/events/EventMetricsVariableDeactivated';
import { EventMetricsVariableLoaded } from 'MetricsReducer/metrics-variables/events/EventMetricsVariableLoaded';
import {
  FilteredMetricsVariable,
  VAR_FILTERED_METRICS_VARIABLE,
} from 'MetricsReducer/metrics-variables/FilteredMetricsVariable';
import { MetricsVariable, VAR_METRICS_VARIABLE } from 'MetricsReducer/metrics-variables/MetricsVariable';
import {
  MetricsVariableFilterEngine,
  type MetricFilters,
} from 'MetricsReducer/metrics-variables/MetricsVariableFilterEngine';
import { MetricsVariableSortEngine } from 'MetricsReducer/metrics-variables/MetricsVariableSortEngine';
import { MetricsList } from 'MetricsReducer/MetricsList/MetricsList';
import { EventFiltersChanged } from 'MetricsReducer/SideBar/sections/MetricsFilterSection/EventFiltersChanged';

import { RelatedListControls } from './RelatedListControls';
import { actionViews } from '../../MetricScene/MetricActionBar';
import { getTrailFor } from '../../shared/utils/utils';
import { getAppBackgroundColor } from '../../shared/utils/utils.styles';
import { signalOnQueryComplete } from '../utils/signalOnQueryComplete';

interface RelatedMetricsSceneState extends SceneObjectState {
  metric: string;
  body: MetricsList;
  listControls: RelatedListControls;
}

export class RelatedMetricsScene extends SceneObjectBase<RelatedMetricsSceneState> {
  constructor({ metric }: { metric: RelatedMetricsSceneState['metric'] }) {
    super({
      metric,
      $variables: new SceneVariableSet({
        variables: [new FilteredMetricsVariable()],
      }),
      $behaviors: [new behaviors.SceneQueryController()],
      key: 'RelatedMetricsScene',
      body: new MetricsList({ variableName: VAR_FILTERED_METRICS_VARIABLE }),
      listControls: new RelatedListControls({}),
    });

    this.addActivationHandler(this.onActivate.bind(this));
  }

  private onActivate() {
    // make sure we display all the available metrics (see DataTrail.tsx, side bar sections in SideBar.tsx and RecentMetricsSection.tsx)
    const metricsVariable = sceneGraph.findByKeyAndType(this, VAR_METRICS_VARIABLE, MetricsVariable);
    metricsVariable.fetchAllMetrics();

    this.subscribeToEvents(metricsVariable);
  }

  private subscribeToEvents(metricsVariable: MetricsVariable) {
    this.initVariablesFilteringAndSorting();

    // Wait for metrics to load, then signal when queries complete
    const sub = metricsVariable.subscribeToState((state) => {
      if (state.loading === false) {
        sub.unsubscribe();

        if (this.state.body.isActive) {
          signalOnQueryComplete(this, actionViews.related);
        } else {
          this.state.body.addActivationHandler(() => {
            signalOnQueryComplete(this, actionViews.related);
          });
        }
      }
    });
  }

  /**
   * The centralized filtering mechanism implemented here is decoupled via the usage of events.
   * In order to work, the variables to be filtered/sorted must emit lifecycle events.
   * This is done via the `withLifecycleEvents` decorator function.
   *
   * For example, check the `FilteredMetricsVariable` class.
   */
  private initVariablesFilteringAndSorting() {
    const { metric } = this.state;

    const enginesMap = new Map<
      string,
      { filterEngine: MetricsVariableFilterEngine; sortEngine: MetricsVariableSortEngine }
    >();

    this.subscribeToEvent(EventMetricsVariableActivated, (event) => {
      // register engines
      const { key } = event.payload;
      const filteredMetricsVariable = sceneGraph.findByKey(this, key) as QueryVariable;

      enginesMap.set(key, {
        filterEngine: new MetricsVariableFilterEngine(filteredMetricsVariable),
        sortEngine: new MetricsVariableSortEngine(filteredMetricsVariable),
      });
    });

    this.subscribeToEvent(EventMetricsVariableDeactivated, (event) => {
      // unregister engines
      enginesMap.delete(event.payload.key);
    });

    const quickSearch = sceneGraph.findByKeyAndType(this, 'quick-search', QuickSearch);

    this.subscribeToEvent(EventMetricsVariableLoaded, (event) => {
      // filter  on initial load
      const { key, options } = event.payload;
      const { filterEngine, sortEngine } = enginesMap.get(key)!;

      filterEngine.setInitOptions(options);

      const filters: Partial<MetricFilters> = {
        names: quickSearch.state.value ? [quickSearch.state.value] : [],
      };

      filterEngine.applyFilters(filters, { forceUpdate: true, notify: false });
      sortEngine.sort('related', { metric });
    });

    /* Filters */

    this.subscribeToEvent(EventQuickSearchChanged, (event) => {
      const { searchText } = event.payload;

      for (const [, { filterEngine, sortEngine }] of enginesMap) {
        filterEngine.applyFilters({ names: searchText ? [searchText] : [] });
        sortEngine.sort('related', { metric });
      }
    });

    this.subscribeToEvent(EventFiltersChanged, (event) => {
      const { type, filters } = event.payload;

      for (const [, { filterEngine, sortEngine }] of enginesMap) {
        filterEngine.applyFilters({ [type]: filters });
        sortEngine.sort('related', { metric });
      }
    });
  }

  public static readonly Component = ({ model }: SceneComponentProps<RelatedMetricsScene>) => {
    const chromeHeaderHeight = useChromeHeaderHeight();
    const trail = getTrailFor(model);
    const styles = useStyles2(getStyles, trail.state.embedded ? 0 : chromeHeaderHeight ?? 0, trail);
    const { $variables, body, listControls } = model.useState();

    return (
      <>
        <div className={styles.searchSticky}>
          <listControls.Component model={listControls} />
        </div>
        <div data-testid="panels-list">
          <body.Component model={body} />
        </div>
        <div className={styles.variables}>
          {$variables?.state.variables.map((variable) => (
            <variable.Component key={variable.state.name} model={variable} />
          ))}
        </div>
      </>
    );
  };
}

function getStyles(theme: GrafanaTheme2, headerHeight: number, trail: DataTrail) {
  return {
    variables: css({
      display: 'none',
    }),
    searchSticky: css({
      margin: theme.spacing(1, 0, 1.5, 0),
      position: 'sticky',
      top: `calc(var(--app-controls-height, 0px) + ${headerHeight}px + var(--action-bar-height, 0px))`,
      zIndex: 10,
      background: getAppBackgroundColor(theme, trail),
      paddingBottom: theme.spacing(1),
    }),
  };
}
